/**
 * <BackToHomePill /> — global floating "Back to home" chip.
 *
 * v35.58 (Jul 2026): replaced the brittle per-page allowlists with runtime
 * detection for logged-out visitors.
 * v35.69 (Jul 2026): the same runtime detection now covers LOGGED-IN users
 * too (the old SHOW_FOR_AUTHED_ON allow-list kept going stale — /trust and
 * 20+ public pages were dead ends for authed visitors). For authed users the
 * pill detects links to `/` OR `/dashboard` OR the authed navbar; if none
 * are visible near the top, it shows a "Back to dashboard" chip. No more
 * allow-list maintenance.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PublicToolNav from './PublicToolNav';

// Routes where the floating pill would distract from the primary funnel.
const SUPPRESS_ON = new Set([
  '/',
  '/login',
  '/signup',
  '/checkout',
  '/checkout/success',
  '/checkout/cancel',
  '/landing-v34-preview',
]);

const BackToHomePill = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [pageHasHomeLink, setPageHasHomeLink] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      // Authed navbar anywhere on the page counts as full navigation.
      if (document.querySelector('[data-testid="navbar-logo-link"]')) {
        setPageHasHomeLink(true);
        return;
      }
      const links = [...document.querySelectorAll('a[href="/"], a[href="/dashboard"]')].filter((a) => {
        if (a.getAttribute('data-testid') === 'back-to-home-pill') return false;
        if (a.closest('[data-testid="public-tool-mobile-nav"]')) return false;
        const r = a.getBoundingClientRect();
        return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < 250;
      });
      setPageHasHomeLink(links.length > 0);
    };
    setPageHasHomeLink(true);
    const t1 = setTimeout(check, 400);
    const t2 = setTimeout(check, 1600);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, [location.pathname]);

  if (loading) return null;

  const path = location.pathname || '/';
  if (SUPPRESS_ON.has(path)) return null;
  if (path.startsWith('/og/')) return null;

  return (
    <>
      {!user && <PublicToolNav />}
      {!pageHasHomeLink && (
        <Link
          to={user ? '/dashboard' : '/'}
          data-testid="back-to-home-pill"
          title={user ? 'Back to dashboard' : 'Back to CARTIEAI home'}
          aria-label={user ? 'Back to dashboard' : 'Back to CARTIEAI home'}
          className={`fixed top-4 left-4 z-[60] items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all hover:-translate-y-0.5 hover:scale-105 ${
            user ? 'inline-flex' : 'hidden md:inline-flex'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{user ? 'Back to dashboard' : 'Back to home'}</span>
        </Link>
      )}
    </>
  );
};

export default BackToHomePill;
